import React, { useMemo, useState } from 'react';
import './visitantes.css';
import { toast } from 'react-toastify';
import { useInternals } from '../../context/internalContext';
import { useAuxiliaries } from '../../context/auxiliariesContext';
import Titulo from '../../components/Titulo/titulo';
import FormattedInput from '../../components/FormatdInput/formatFunctions';
import { TYPE_OAB_OPTIONS, resolveTypeOab } from '../../utils/typeOab';

const normalizeText = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/\s+/g, ' ')
  .trim()
  .toUpperCase();

const resolveOabStatusVariant = (situacao) => {
  const normalized = normalizeText(situacao);
  if (normalized.includes('REGULAR')) return 'regular';
  if (normalized.includes('CANCELAD')) return 'cancelado';
  if (normalized.includes('FALECID')) return 'falecido';
  return 'outro';
};

const VISITOR_ENDPOINTS = {
  others: '/internals/visitor/others/',
  publicservante: '/internals/visitor/publicservant/',
  lawyer: '/internals/visitor/lawyer/',
};

const VISITOR_TYPE_RULES = {
  lawyer: new Set([
    normalizeText('Advogado'),
  ]),
  publicservante: new Set([
    normalizeText('Policial'),
    normalizeText('Promotor de Justiça'),
    normalizeText('Membro do Ministério Público'),
    normalizeText('Juiz'),
    normalizeText('Defensor Público'),
    normalizeText('Membro da Defensoria Pública'),
  ]),
  others: new Set([
    normalizeText('Religioso'),
    normalizeText('Terceirizado'),
    normalizeText('Outros Serviços'),
  ]),
};

const resolveVisitorCategory = (typeName) => {
  const normalized = normalizeText(typeName);
  if (VISITOR_TYPE_RULES.lawyer.has(normalized)) return 'lawyer';
  if (VISITOR_TYPE_RULES.publicservante.has(normalized)) return 'publicservante';
  if (VISITOR_TYPE_RULES.others.has(normalized)) return 'others';
  return null;
};

const buildInitialFormData = () => ({
  type_visitor: '',
  name: '',
  surname: '',
  email: '',
  oab: '',
  uf_oab: '',
  type_oab: '',
  password: '',
  phone: '',
  registration: '',
  cpf: '',
});

function AddVisitorOthers({ onSuccess }) {
  const { createVisitorOthers, consultLawyerOab } = useInternals();
  const { listTypeVisitor } = useAuxiliaries();

  const [formData, setFormData] = useState(buildInitialFormData());
  const [visitorTypes, setVisitorTypes] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [oabLookup, setOabLookup] = useState({ status: 'idle', info: null });

  const selectedVisitorType = useMemo(
    () => visitorTypes.find((type) => String(type.id) === String(formData.type_visitor)),
    [visitorTypes, formData.type_visitor]
  );

  const selectedTypeName = selectedVisitorType?.name || '';
  const selectedCategory = resolveVisitorCategory(selectedTypeName);

  const allowedVisitorTypes = useMemo(
    () => visitorTypes.filter((type) => resolveVisitorCategory(type?.name)),
    [visitorTypes]
  );

  // Carregar tipos de visitante permitidos para este fluxo.
  React.useEffect(() => {
    const fetchVisitorTypes = async () => {
      try {
        const response = await listTypeVisitor();
        setVisitorTypes(response?.results || response || []);
      } catch (error) {
        console.error('Erro ao carregar tipos de visitante:', error);
      }
    };

    fetchVisitorTypes();
  }, [listTypeVisitor]);

  // Consulta automática da situação da OAB assim que número e UF forem informados.
  // Não bloqueia o cadastro: só preenche nome/email e mostra a situação encontrada.
  React.useEffect(() => {
    if (selectedCategory !== 'lawyer') {
      setOabLookup({ status: 'idle', info: null });
      return;
    }

    const registration = formData.oab.trim();
    const uf = formData.uf_oab.trim();

    if (!registration || uf.length !== 2) {
      setOabLookup({ status: 'idle', info: null });
      return;
    }

    let cancelled = false;
    setOabLookup((prev) => ({ ...prev, status: 'loading' }));

    const timer = setTimeout(async () => {
      const result = await consultLawyerOab(registration, uf);
      if (cancelled) return;

      const inscricoes = result?.data?.inscricoes;
      const match = Array.isArray(inscricoes)
        ? inscricoes.find((item) => normalizeText(item?.uf) === normalizeText(uf)) || inscricoes[0]
        : null;

      if (result?.success && match) {
        setOabLookup({
          status: 'found',
          info: {
            situacao: match.situacao || '',
            tipo: match.tipo || '',
            uf: match.uf || uf,
            numero: match.numero || registration,
          },
        });

        setFormData((prev) => {
          const updated = { ...prev };
          if (result.data.nome) {
            const parts = String(result.data.nome).trim().split(/\s+/);
            updated.name = parts[0] || '';
            updated.surname = parts.length > 1 ? parts.slice(1).join(' ') : '';
          }
          if (result.data.cpf) {
            const cpfDigits = String(result.data.cpf).replace(/\D/g, '').slice(0, 11);
            updated.cpf = cpfDigits
              .replace(/(\d{3})(\d)/, '$1.$2')
              .replace(/(\d{3})(\d)/, '$1.$2')
              .replace(/(\d{3})(\d{1,2})/, '$1-$2');
          }
          if (match.email) {
            updated.email = match.email;
          }
          const typeOab = resolveTypeOab(match.tipo);
          if (typeOab) {
            updated.type_oab = typeOab;
          }
          return updated;
        });
      } else {
        setOabLookup({ status: 'not_found', info: null });
      }
    }, 600);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [selectedCategory, formData.oab, formData.uf_oab, consultLawyerOab]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === 'uf_oab') {
        updated.uf_oab = value.replace(/[^a-zA-Z]/g, '').toUpperCase().slice(0, 2);
      }
      return updated;
    });
  };

  const buildPayloadByCategory = () => {
    if (!selectedCategory || !selectedTypeName) return null;
    const selectedTypeId = Number(formData.type_visitor);

    if (!Number.isInteger(selectedTypeId) || selectedTypeId <= 0) {
      return null;
    }

    if (selectedCategory === 'others') {
      return {
        name: formData.name,
        type_visitor: selectedTypeId,
        cpf: formData.cpf,
      };
    }

    if (selectedCategory === 'publicservante') {
      return {
        name: formData.name,
        type_visitor: selectedTypeId,
        phone: formData.phone,
        registration: formData.registration,
        cpf: formData.cpf,
      };
    }

    if (selectedCategory === 'lawyer') {
      return {
        name: formData.name,
        surname: formData.surname,
        email: formData.email,
        oab: formData.oab,
        uf_oab: formData.uf_oab,
        type_oab: formData.type_oab,
        cpf: formData.cpf,
        password: formData.oab,
      };
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!selectedCategory) {
      toast.error('Tipo de visitante inválido para este cadastro.');
      setIsSubmitting(false);
      return;
    }

    if (selectedCategory === 'lawyer' && !resolveTypeOab(formData.type_oab)) {
      toast.error('Selecione o tipo de inscrição na OAB.');
      setIsSubmitting(false);
      return;
    }

    const endpoint = VISITOR_ENDPOINTS[selectedCategory];
    const payload = buildPayloadByCategory();

    if (!payload) {
      toast.error('Não foi possível montar os dados de envio.');
      setIsSubmitting(false);
      return;
    }

    try {
      const createdVisitor = await createVisitorOthers(payload, endpoint);
      setFormData(buildInitialFormData());
      if (typeof onSuccess === 'function') {
        onSuccess(createdVisitor);
      }
    } catch (error) {
      console.error('Erro ao cadastrar visitante:', error?.response?.data || error?.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <form onSubmit={handleSubmit} className='visitor-form'>
        <Titulo children={<h2>Cadastro visitantes</h2>} />
        <div className='items'>
          <div className='form-group'>
            <label>Tipo de visitante:</label>
            <select
              name='type_visitor'
              value={formData.type_visitor}
              onChange={handleInputChange}
              required
              className='form-select'
            >
              <option value=''>Selecione o tipo</option>
              {allowedVisitorTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>

          {selectedCategory === 'lawyer' ? (
            <>
              <div className='form-group-duplo'>
                <div className='form-group'>
                  <label>Número da OAB:</label>
                  <input
                    type='text'
                    name='oab'
                    value={formData.oab}
                    onChange={handleInputChange}
                    required
                    className='form-input'
                    placeholder='Digite o número da OAB'
                  />
                </div>
                <div className='form-group'>
                  <label>UF da OAB:</label>
                  <input
                    type='text'
                    name='uf_oab'
                    value={formData.uf_oab}
                    onChange={handleInputChange}
                    required
                    maxLength={2}
                    className='form-input'
                    placeholder='Digite a UF da OAB'
                  />
                </div>
              </div>

              <div className='form-group'>
                <label>Tipo de inscrição na OAB:</label>
                <select
                  name='type_oab'
                  value={formData.type_oab}
                  onChange={handleInputChange}
                  required
                  className='form-select'
                >
                  <option value=''>Selecione o tipo de inscrição</option>
                  {TYPE_OAB_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {oabLookup.status === 'loading' && (
                <p className='oab-lookup-hint oab-lookup-hint-loading'>
                  <span className='oab-lookup-spinner' /> Consultando situação da OAB...
                </p>
              )}
              {oabLookup.status === 'not_found' && (
                <p className='oab-lookup-hint'>Não foi possível localizar essa inscrição na OAB.</p>
              )}
              {oabLookup.status === 'found' && oabLookup.info && (
                <div className={`oab-lookup-box oab-status-${resolveOabStatusVariant(oabLookup.info.situacao)}`}>
                  <strong>Situação da OAB: {oabLookup.info.situacao || 'Não informada'}</strong>
                  <span>{[oabLookup.info.tipo, `OAB/${oabLookup.info.uf} ${oabLookup.info.numero}`].filter(Boolean).join(' - ')}</span>
                  <p>Informe essa situação ao advogado.</p>
                </div>
              )}

              <div className='form-group-duplo'>
                <div className='form-group'>
                  <label>Nome:</label>
                  <FormattedInput
                    type='nome'
                    name='name'
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className='form-input'
                  />
                </div>
                <div className='form-group'>
                  <label>Sobrenome:</label>
                  <FormattedInput
                    type='nome'
                    name='surname'
                    value={formData.surname}
                    onChange={handleInputChange}
                    required
                    className='form-input'
                  />
                </div>
              </div>

              <div className='form-group-duplo'>
                <div className='form-group'>
                  <label>Email:</label>
                  <input
                    type='email'
                    name='email'
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className='form-input'
                  />
                </div>
                <div className='form-group'>
                  <label>CPF:</label>
                  <FormattedInput
                    type='cpf'
                    name='cpf'
                    value={formData.cpf}
                    onChange={handleInputChange}
                    required
                    className='form-input'
                  />
                </div>
              </div>
            </>
          ) : (
            <div className='form-group'>
              <label>Nome:</label>
              <FormattedInput
                type='nome'
                name='name'
                value={formData.name}
                onChange={handleInputChange}
                required
                className='form-input'
              />
            </div>
          )}

          {selectedCategory === 'publicservante' && (
            <>
              <div className='form-group-duplo'>
                <div className='form-group'>
                  <label>Telefone:</label>
                  <FormattedInput
                    type='telefone'
                    name='phone'
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className='form-input'
                  />
                </div>
                <div className='form-group'>
                  <label>Matrícula:</label>
                  <input
                    type='text'
                    name='registration'
                    value={formData.registration}
                    onChange={handleInputChange}
                    required
                    className='form-input'
                  />
                </div>
              </div>
            </>
          )}

          {(selectedCategory === 'others' || selectedCategory === 'publicservante') && (
            <div className='form-group'>
              <label>CPF:</label>
              <FormattedInput
                type='cpf'
                name='cpf'
                value={formData.cpf}
                onChange={handleInputChange}
                required
                className='form-input'
              />
            </div>
          )}
        </div>

        <button type='submit' className='add-interno-btn' disabled={isSubmitting}>
          {isSubmitting ? (
            <span className='visitante-btn-loading'>
              <span className='visitante-btn-spinner' /> Enviando...
            </span>
          ) : (
            'SALVAR'
          )}
        </button>
      </form>
    </div>
  );
}

export default AddVisitorOthers;
